import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import type { FieldValues } from '@/api/generated/model';
import { FieldsForm, FormPage, initialFieldValues } from '@/components/manage';
import { ErrorState, LoadingState } from '@/components';
import { SportSettingsForm } from '@/components/sport-settings';
import { useTournament, useTournamentSettings, useUpdateTournamentSettings } from '@/hooks/useApi';
import { useSettingsForm, useUpdateSettingsForm } from '@/hooks/useManage';
import { useNotification } from '@/contexts/NotificationContext';
import { getApiErrorMessage, getApiFieldErrors } from '@/utils/apiError';
import type { SportSettings } from '@/types';

/** Настройки турнира: поля пресета с сервера (`settings-form`) + правила вида спорта. */
export const TournamentSettingsPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { data: tournament } = useTournament(id);
  const { data: form, isLoading, error, refetch } = useSettingsForm(id);
  const { data: sportSettings } = useTournamentSettings(id);
  const updateForm = useUpdateSettingsForm(id);
  const updateSportSettings = useUpdateTournamentSettings();
  const [values, setValues] = useState<FieldValues | null>(null);
  const [rules, setRules] = useState<SportSettings | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (isLoading) return <LoadingState fullscreen message="Загрузка настроек..." />;
  if (error || !form) return <ErrorState message="Не удалось загрузить настройки" onRetry={refetch} />;

  const current = values ?? initialFieldValues(form.fields);
  const dirty = values !== null || rules !== null;
  const saving = updateForm.isPending || updateSportSettings.isPending;

  const submit = async () => {
    setErrors({});
    try {
      if (rules && tournament) {
        await updateSportSettings.mutateAsync({ id, data: { ...rules, sportType: tournament.sportType } });
      }
      if (values) {
        await updateForm.mutateAsync(values);
      }
      showSuccess('Настройки сохранены');
      navigate(`/tournaments/${id}?tab=workflow`, { replace: true });
    } catch (err) {
      const fieldErrors = getApiFieldErrors(err);
      if (Object.keys(fieldErrors).length) setErrors(fieldErrors);
      else showError(getApiErrorMessage(err, 'Не удалось сохранить настройки.'));
    }
  };

  return (
    <FormPage
      title={tournament ? `Настройки: ${tournament.name}` : 'Настройки турнира'}
      onSubmit={() => void submit()}
      saving={saving}
      submitDisabled={!dirty}
      dirty={dirty}
      closeTo={`/tournaments/${id}`}
    >
      <Box sx={{ pt: 1 }}>
        <FieldsForm fields={form.fields} values={current} onChange={setValues} errors={errors} sportType={tournament?.sportType} />
        {tournament && (
          <Accordion disableGutters sx={{ mt: 2, bgcolor: 'inherit' }}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 48 }}>
              <Typography fontWeight={600}>Правила вида спорта</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SportSettingsForm
                sportType={tournament.sportType}
                value={rules ?? sportSettings ?? { sportType: tournament.sportType }}
                onChange={setRules}
              />
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </FormPage>
  );
};
